import { isEmpty, isUndefined } from 'lodash';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { SettingsTabs } from '../../pages/Settings/Settings.enum';
import { IS_PRODUCTION, IS_PRODUCTION_OR_STAGING } from '../../constants/envs';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { eUserRole } from '../../providers/UserProvider/User.enums';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider/Subscription.context';
import { eSubscriptionStatus } from '../../providers/SubscriptionProvider/Subscription.enums';
import { useFeatureFlagsContext } from '../../providers/FeatureFlagsProvider/FeatureFlags.context';
import { getCompletedKey } from '../../pages/Onboarding/utils/onboarding-storage-keys';
import { PublicRoutes, RouterPaths } from '../Router/Router.constants';

export default function AuthMiddleware() {
  const { hasSession, currentUser, currentUserRole } = useUserContext();
  const {
    currentWorkspaceId,
    workspaceSaleSource,
    setWorkspaceSaleSource,
    workspaceInfo,
    isLoadingWorkspaceInfo,
  } = useWorkspaceContext();
  const { subscription, isLoadingSubscription } = useSubscriptionContext();
  const { isOnboardingAutoSetupEnabled, isLoadingFeatureFlags } =
    useFeatureFlagsContext();
  const location = useLocation();
  const isPublicRoute = PublicRoutes.includes(location.pathname);

  // If it's a public route and user is not authenticated, allow access
  if (isPublicRoute && !hasSession) {
    return <Outlet />;
  }

  // If not authenticated and trying to access private route, redirect to login
  if (!hasSession && !isPublicRoute) {
    window.location.href = RouterPaths.signIn;
    return;
  }

  // If authenticated but missing profile info, redirect to signup (unless already there).
  // EXCEPT for the activate route (BUG-800): an invited teammate who tried a
  // sign-in door first is signed in without a profile, and activation is where
  // that profile gets completed. Sending them to signup closed the emailed link.
  if (
    hasSession &&
    !isUndefined(currentUser) &&
    (!currentUser?.firstName || !currentUser?.phoneNumber) &&
    location.pathname !== RouterPaths.signUp &&
    location.pathname !== RouterPaths.activate &&
    isUndefined(currentWorkspaceId)
  ) {
    window.location.href = RouterPaths.signUp;
    return;
  }

  // If authenticated with profile but no workspace, redirect to workspace creation (unless already there)
  // Check both currentUser.workspaces and currentWorkspaceId from localStorage
  // This handles the case where workspace is set but not yet in user's workspaces array
  // EXCEPT for the activate route (BUG-800): an invitee whose account already
  // existed has a profile and no workspace until the invitation is accepted there.
  if (
    hasSession &&
    currentUser?.firstName &&
    currentUser?.phoneNumber &&
    isEmpty(currentUser?.workspaces) &&
    !currentWorkspaceId &&
    location.pathname !== RouterPaths.createWorkspace &&
    location.pathname !== RouterPaths.activate
  ) {
    window.location.href = RouterPaths.createWorkspace;
    return;
  }

  // If user is authenticated but on public route and has workspace, redirect to dashboard
  // EXCEPT for activate route - allow it to handle its own redirect after activation
  if (
    hasSession &&
    isPublicRoute &&
    !isUndefined(currentWorkspaceId) &&
    location.pathname !== RouterPaths.activate
  ) {
    if (workspaceSaleSource === 'Pricing_LPprecios') {
      setWorkspaceSaleSource(undefined);
      window.location.href = `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=Pricing_LPprecios`;
      return;
    }

    window.location.href = RouterPaths.dashboard;
    return;
  }

  // Wait for feature flags and workspace info to resolve before making onboarding decisions.
  // Workspace info is authoritative for `onboardingCompleted`; evaluating the auto-setup
  // redirect before it loads would treat `workspaceInfo?.onboardingCompleted` as falsy
  // and bounce users who have already completed onboarding. Gate on the explicit
  // `isLoadingWorkspaceInfo` flag rather than `workspaceInfo === undefined` so a failed
  // fetch does not leave the user stuck on a blank screen — the flag flips to `false`
  // in the finally block regardless of success or failure.
  //
  // Scope the gate narrowly: only block when the current route evaluation actually
  // depends on `workspaceInfo`. On `/onboarding` we always need it (production re-entry
  // guard + fail-closed flag check both read the workspace entity). Off `/onboarding`
  // we only need it when the auto-setup redirect could fire — i.e., when every
  // precondition (flag enabled, subscription resolved + ACTIVE-or-TRIAL, role OWNER)
  // is already met. SELLERs, PAST_DUE/CANCELLED/PAUSED accounts, and flag-off
  // workspaces are never subject to the redirect, so forcing them to wait on the
  // workspace fetch before their dashboard renders adds perceived latency for no
  // correctness benefit. TRIAL must mirror ACTIVE here (matching the redirect guard
  // at lines 152–164), otherwise a TRIAL OWNER skips the loading gate and the
  // redirect at lines 152–164 evaluates against `workspaceInfo === undefined`,
  // bouncing them to /onboarding before the production re-entry guard immediately
  // bounces them back to /dashboard.
  const couldAutoSetupRedirect =
    isOnboardingAutoSetupEnabled &&
    !isLoadingSubscription &&
    (subscription.status === eSubscriptionStatus.ACTIVE ||
      subscription.status === eSubscriptionStatus.TRIAL) &&
    currentUserRole === eUserRole.OWNER &&
    location.pathname !== RouterPaths.onboarding;

  if (
    (isLoadingFeatureFlags || isLoadingWorkspaceInfo) &&
    (location.pathname === RouterPaths.onboarding || couldAutoSetupRedirect)
  ) {
    return null;
  }

  // Block access to /onboarding when the runtime feature flag is OFF (fail-closed).
  // Users navigating directly to /onboarding without the flag are redirected to dashboard.
  // Only enforce after workspace is loaded — before that, flags default to false and would
  // incorrectly redirect users who are legitimately starting onboarding.
  // DEV escape hatch: local builds (VITE_ENV=local) skip this gate so testers can
  // re-enter the onboarding flow via the DevToolbar without flipping a workspace
  // feature flag. Production and staging both keep the gate active. IS_PRODUCTION_OR_STAGING
  // is keyed on the explicit VITE_ENV variable (matching the sibling re-entry guard
  // at L144 below) instead of import.meta.env.DEV, which would silently flip true
  // on any Vite build issued with --mode=development or --mode=staging.
  if (
    IS_PRODUCTION_OR_STAGING &&
    hasSession &&
    currentWorkspaceId &&
    location.pathname === RouterPaths.onboarding &&
    !isOnboardingAutoSetupEnabled
  ) {
    return <Navigate to={RouterPaths.dashboard} replace />;
  }

  // Production guard: prevent re-entry to onboarding when already completed.
  // In staging/local the guard is inactive so the team can re-test the onboarding flow
  // with the same account. Uses onboardingCompleted from the workspace entity
  // (derived from onboarding_completed_at in workspace-service) as the source of truth,
  // with localStorage as a fallback for the window before the next workspace fetch.
  if (
    IS_PRODUCTION &&
    hasSession &&
    currentWorkspaceId &&
    location.pathname === RouterPaths.onboarding &&
    (workspaceInfo?.onboardingCompleted ||
      localStorage.getItem(getCompletedKey(currentWorkspaceId)))
  ) {
    return <Navigate to={RouterPaths.dashboard} replace />;
  }

  // Post-payment onboarding: redirect users with an active-or-trialing paid plan
  // who haven't completed onboarding.
  // Controlled by the runtime workspace feature flag (onboarding_auto-set-up).
  // Uses workspaceInfo.onboardingCompleted (derived from onboarding_completed_at on the workspace
  // entity) as the primary source of truth, localStorage as a fallback for the race window
  // between local completion and the next workspace refetch. Matching the RET-796 re-entry guard
  // above keeps the two guards from disagreeing, which would otherwise cause a redirect loop.
  // Uses Navigate (client-side) instead of window.location.href to avoid a full page reload
  // that re-initializes all providers and triggers redundant API calls.
  //
  // Includes TRIAL alongside ACTIVE because Stripe payment links create
  // subscriptions in `trialing` status until the first real charge — a paid
  // PLAN_PROFESSIONAL/ADVANCED workspace can sit in TRIAL for the full trial
  // window. Matching the workspace-service-side `SubscriptionSummary::isActive()`
  // (which already includes TRIAL) keeps the FE gate aligned with the BE source
  // of truth and unblocks the post-payment-link onboarding flow.
  if (
    isOnboardingAutoSetupEnabled &&
    hasSession &&
    currentWorkspaceId &&
    !isLoadingSubscription &&
    (subscription.status === eSubscriptionStatus.ACTIVE ||
      subscription.status === eSubscriptionStatus.TRIAL) &&
    currentUserRole === eUserRole.OWNER &&
    location.pathname !== RouterPaths.onboarding &&
    workspaceInfo?.onboardingCompleted === false &&
    !localStorage.getItem(getCompletedKey(currentWorkspaceId))
  ) {
    return <Navigate to={RouterPaths.onboarding} replace />;
  }

  // Default: allow access to the requested route
  return <Outlet />;
}
